import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { PasswordInput } from "@/components/ui/password-input";
import {
	type ResetPasswordFormData,
	resetPasswordSchema,
} from "@/features/auth/schemas/password-reset";
import { auth } from "@/lib/auth";

export const Route = createFileRoute("/_auth/_auth-layout/reset-password")({
	validateSearch: (search) => ({
		token: typeof search.token === "string" ? search.token : "",
		error: typeof search.error === "string" ? search.error : "",
	}),
	component: ResetPassword,
});

function ResetPassword() {
	const passwordId = useId();
	const confirmPasswordId = useId();
	const { token, error: tokenError } = Route.useSearch();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<ResetPasswordFormData>({
		resolver: zodResolver(resetPasswordSchema),
		defaultValues: {
			newPassword: "",
			confirmPassword: "",
		},
	});

	async function onSubmit(data: ResetPasswordFormData) {
		if (!token) return;

		setError(null);
		setIsSubmitting(true);

		try {
			const result = await auth.resetPassword({
				token,
				newPassword: data.newPassword,
			});

			if (result.error) {
				throw new Error(result.error.message || "Falha ao redefinir senha");
			}

			setSuccess(true);
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: "Não foi possível redefinir sua senha.",
			);
		} finally {
			setIsSubmitting(false);
		}
	}

	const invalidToken = tokenError || !token;

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col items-center gap-2 text-center">
				<h1 className="text-xl font-bold">Nova senha</h1>
				<FieldDescription>Defina uma nova senha para acessar o painel.</FieldDescription>
			</div>

			{invalidToken ? (
				<FieldGroup>
					<Field>
						<div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
							Link inválido ou expirado. Solicite uma nova redefinição de senha.
						</div>
					</Field>
					<FieldDescription className="text-center">
						<Link to="/forgot-password" className="underline">
							Solicitar novo link
						</Link>
					</FieldDescription>
				</FieldGroup>
			) : success ? (
				<FieldGroup>
					<Field>
						<div className="rounded-md bg-green-50 p-3 text-sm text-green-700">
							Senha redefinida com sucesso.
						</div>
					</Field>
					<FieldDescription className="text-center">
						<Link to="/signin" className="underline">
							Entrar com a nova senha
						</Link>
					</FieldDescription>
				</FieldGroup>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<FieldGroup>
						<Field>
							<FieldLabel htmlFor={passwordId}>Nova senha</FieldLabel>
							<PasswordInput
								id={passwordId}
								placeholder="Digite sua nova senha"
								{...register("newPassword")}
							/>
							<FieldError
								errors={errors.newPassword ? [errors.newPassword] : []}
							/>
						</Field>
						<Field>
							<FieldLabel htmlFor={confirmPasswordId}>Confirmar senha</FieldLabel>
							<PasswordInput
								id={confirmPasswordId}
								placeholder="Confirme sua nova senha"
								{...register("confirmPassword")}
							/>
							<FieldError
								errors={
									errors.confirmPassword ? [errors.confirmPassword] : []
								}
							/>
						</Field>
						{error && (
							<Field>
								<div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
									{error}
								</div>
							</Field>
						)}
						<Field>
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting ? "Salvando..." : "Redefinir senha"}
							</Button>
						</Field>
					</FieldGroup>
				</form>
			)}
		</div>
	);
}
